import { styled } from '@mui/material/styles';
import { Box, GridLegacy as Grid, Link, Stack, ButtonBase } from '@mui/material';
import siloRealLogo from '../../../assets/logos/siloreal-logo-black.webp';
import plastarLogo from '../../../assets/logos/plastar-logo.webp';
import i18n from '../../../libs/language';
import { useEffectOnMount } from '@old/hooks/core';
import { useGlobalStore } from '@old/global-store/use-store';
import { SILOREAL_WEBSITE_VERSION } from '@old/data/constants';
import type { PropsWithChildren } from 'react';
import { WhattsAppButton } from '@old/features/whattsapp-luna/whattsapp-button';

import backgroundImage from '../../../assets/images/field-background.webp';
import plastarBgImage from '../../../assets/images/plastar-background.jpg';
import neraBgImage from '../../../assets/images/nera-background.webp';
import agrologyBgImage from '../../../assets/images/agrology-background.jpg';
import lartirigoyenBgImage from '../../../assets/images/lartirigoyen-background.webp';
import edpBgImage from '../../../assets/images/edp-background.webp';

type AuthLayoutProps = {
  variant?: 'siloreal' | 'plastar' | 'nera' | 'agrology' | 'lartirigoyen' | 'edp';
};

const AuthLayoutComponent = (props: PropsWithChildren<AuthLayoutProps>) => {
  const { children, variant = 'siloreal' } = props;
  const { setLanguage, preferedLanguageId } = useGlobalStore();

  const handleLanguageChange = (lang: 'es' | 'pt') => {
    i18n.locale = lang;
    setLanguage(lang);
  };

  const CORE = {
    background: {
      siloreal: {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        height: '100vh',
        width: '100vw',
        minHeight: 300,
      },
      plastar: {
        backgroundImage: `url(${plastarBgImage})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        height: '100vh',
        width: '100vw',
        minHeight: 300,
      },
      nera: {
        backgroundImage: `url(${neraBgImage})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: '80% 50%',
        height: '100vh',
        width: '100vw',
        minHeight: 300,
      },
      agrology: {
        backgroundImage: `url(${agrologyBgImage})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: '80% 50%',
        height: '100vh',
        width: '100vw',
        minHeight: 300,
      },
      lartirigoyen: {
        backgroundImage: `url(${lartirigoyenBgImage})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: '45% 50%',
        height: '100vh',
        width: '100vw',
        minHeight: 300,
      },
      edp: {
        backgroundImage: `url(${edpBgImage})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: '45% 50%',
        height: '100vh',
        width: '100vw',
        minHeight: 300,
      },
    },

    branding: {
      props: {
        plastar: {
          src: plastarLogo,
          alt: 'Plastar Logo',
          style: { width: '32%', height: '32%', objectFit: 'contain' },
        },
        nera: {
          src: 'https://iofbucket.s3.amazonaws.com/logo/Logo%20Nera%20RGB.svg',
          alt: 'Nera Logo',
          style: { width: '28%', height: '28%', objectFit: 'contain' },
        },
        agrology: {
          src: 'https://iofbucket.s3.amazonaws.com/logo/Agrology%20Logo%20Black.svg',
          alt: 'Agrology Logo',
          style: { width: '28%', height: '28%', objectFit: 'contain' },
        },
        lartirigoyen: {
          src: 'https://iofbucket.s3.amazonaws.com/logo/Logo%20Lartirigoyen.svg',
          alt: 'Lartirigoyen Logo',
          style: { width: '35%', height: '35%', objectFit: 'contain' },
        },
        edp: {
          src: 'https://iofbucket.s3.amazonaws.com/logo/Logo%20Edp%20Color.svg',
          alt: 'Edp Logo',
          style: { width: '25%', height: '25%', objectFit: 'contain' },
        },
      },
      silorealLogoAdjust: {
        siloreal: '32%',
        plastar: '25%',
        nera: '28%',
        agrology: '28%',
        lartirigoyen: '25%',
        edp: '30%',
      },
    },
    styles: {
      color: {
        siloreal: 'rgba(255, 255, 255, 0.6)',
        plastar: 'rgba(0, 0, 0, 0.2)',
        nera: 'rgba(255, 255, 255, 0.8)',
        agrology: 'rgba(255, 255, 255, 0.8)',
        lartirigoyen: 'rgba(255, 255, 255, 0.8)',
        edp: 'rgba(255, 255, 255, 0.6)',
      },
      flex: {
        siloreal: 1.4,
        plastar: 2.5,
        nera: 1.67,
        agrology: 0.75,
        lartirigoyen: 0.75,
        edp: 0.6,
      },
    },
  } as const;

  const bgContent = CORE.background[variant];

  const StyledGrid = styled(Grid)(bgContent);

  useEffectOnMount(() => {
    // IMPORTANTE: los effects de hijos se ejecutan ANTES que los del padre en React (bottom-up).
    // Si leemos `preferedLanguageId` del closure, obtenemos el valor del último render
    // (antes de que el hijo haya podido llamar setLanguage desde un ?lang= URL param).
    // useGlobalStore.getState() lee el store de Zustand de forma SINCRÓNICA y LIVE:
    // captura cualquier setLanguage() que ya haya ejecutado un componente hijo.
    const currentLang = useGlobalStore.getState().preferedLanguageId;

    // 1. Si el store ya tiene un idioma válido (por botón manual o por URL param en hijo), respetarlo.
    if (currentLang === 'pt' || currentLang === 'es') {
      i18n.locale = currentLang;
      return;
    }
    // 2. Preferencia guardada en localStorage (login previo o flujo verify/reset con lang param)
    const stored = localStorage.getItem('LANGUAGE_PREFERENCE');
    if (stored === 'pt' || stored === 'es') {
      i18n.locale = stored;
      setLanguage(stored);
      return;
    }
    // 3. Fallback: idioma del navegador, solo si no hay nada previo
    const browserLanguage = navigator.language;
    switch (browserLanguage) {
      case 'pt':
      case 'pt-BR':
        i18n.locale = 'pt';
        setLanguage(i18n.locale);
        localStorage.setItem('LANGUAGE_PREFERENCE', 'pt');
        break;
      case 'es':
      case 'es-ES':
        i18n.locale = 'es';
        setLanguage(i18n.locale);
        localStorage.setItem('LANGUAGE_PREFERENCE', 'es');
        break;
    }
  });

  const currentYear = new Date().getFullYear();

  return (
    <Grid
      container
      direction={{ xs: 'column', md: 'row' }}
      sx={{
        minHeight: '100vh',
        height: { md: '100vh' },
        overflow: { md: 'hidden' },
      }}
    >
      <StyledGrid item xs={5}>
        <Stack height="100%">
          <Box flex={1} />
          <Box flex={CORE.styles.flex[variant]}>
            <Stack
              direction="row"
              gap={14}
              py={3}
              justifyContent="center"
              alignItems="center"
              sx={{
                backgroundColor: CORE.styles.color[variant],
                backdropFilter: 'blur(8px)',
              }}
            >
              {variant !== 'siloreal' ? <img {...CORE.branding.props[variant]} /> : null}
              <img
                src={siloRealLogo}
                alt="logo"
                style={{
                  width: CORE.branding.silorealLogoAdjust[variant],
                  height: CORE.branding.silorealLogoAdjust[variant],
                  objectFit: 'contain',
                }}
              />
            </Stack>
          </Box>
        </Stack>
      </StyledGrid>
      <Grid
        item
        xs={7}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: '100vh', md: '100%' },
          height: { md: '100%' },
          overflow: { md: 'hidden' },
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, pt: 1.5, pl: 2 }}>
          <ButtonBase
            data-testid="lang-btn-es"
            onClick={() => handleLanguageChange('es')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              fontWeight: preferedLanguageId === 'es' ? 700 : 400,
              color: preferedLanguageId === 'es' ? '#222' : '#aaa',
              backgroundColor: preferedLanguageId === 'es' ? 'rgba(116,185,255,0.15)' : 'transparent',
              border: preferedLanguageId === 'es' ? '1.5px solid #74b9ff' : '1.5px solid #ddd',
              borderRadius: '6px',
              px: '9px',
              py: '4px',
              lineHeight: 1.4,
              transition: 'all 0.15s ease',
              '&:hover': { color: '#222', borderColor: '#74b9ff', backgroundColor: 'rgba(116,185,255,0.10)' },
            }}
          >
            🇦🇷 Español
          </ButtonBase>
          <ButtonBase
            data-testid="lang-btn-pt"
            onClick={() => handleLanguageChange('pt')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              fontWeight: preferedLanguageId === 'pt' ? 700 : 400,
              color: preferedLanguageId === 'pt' ? '#222' : '#aaa',
              backgroundColor: preferedLanguageId === 'pt' ? 'rgba(85,239,196,0.15)' : 'transparent',
              border: preferedLanguageId === 'pt' ? '1.5px solid #00b894' : '1.5px solid #ddd',
              borderRadius: '6px',
              px: '9px',
              py: '4px',
              lineHeight: 1.4,
              transition: 'all 0.15s ease',
              '&:hover': { color: '#222', borderColor: '#00b894', backgroundColor: 'rgba(85,239,196,0.10)' },
            }}
          >
            🇧🇷 Português
          </ButtonBase>
        </Stack>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            sx={{
              flex: 1,
              maxWidth: '95%',
              width: '100%',
              mx: 'auto',
              mt: 2,
              py: 2,
            }}
          >
            {children}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexShrink: 0,
              mt: 2,
              mb: 2,
            }}
          >
            <WhattsAppButton />
          </Box>
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            flexShrink: 0,
            py: 1.5,
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <Link
            variant="body2"
            style={{ color: 'black', fontWeight: 300, textDecoration: 'none', marginLeft: '20px' }}
          >
            ©{currentYear} SiloReal v{SILOREAL_WEBSITE_VERSION}
          </Link>
          <Stack
            direction="row"
            justifyContent="space-around"
            spacing={2}
            marginRight="20px"
            alignItems="center"
          >
            <Link
              variant="body2"
              data-testid="privacy-policy-link"
              sx={{ color: 'black', textDecoration: 'none' }}
              href="https://iof-company.notion.site/Pol-tica-de-Privacidad-b703d03af18e42bb8229d24229c58d50"
              target="_blank"
            >
              {i18n.t('app.public_pages.policy_privacy')}
            </Link>
            <Link
              variant="body2"
              data-testid="terms-conditions-link"
              sx={{ color: 'black', textDecoration: 'none' }}
              href="https://iof-company.notion.site/T-rminos-y-Condiciones-Generales-9377d150dc764653b412fd68f80af8af"
              target="_blank"
            >
              {i18n.t('app.public_pages.terms_conditions')}
            </Link>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default AuthLayoutComponent;
